/**
 * DiscoverSelection — the user's complete filter selection as one typed value.
 *
 * Selection is what the user chose (filters, activities, search text);
 * location is resolution, not selection, and deliberately lives outside this
 * type (see CONTEXT.md). Parsing is total: every field is present after
 * `parseSelection`, with defaults applied and multi-select arrays in registry
 * value order. Serialization is sparse: `serializeSelection` omits anything
 * equal to its default, so each selection has exactly one canonical URL
 * spelling. Flat params are the only written encoding; the legacy `f` JSON
 * blob is read-only input and flat params win over it.
 *
 * Parsing is lenient per field — an invalid value falls back to that field's
 * default and never affects siblings, including fields inside `f`.
 *
 * Design notes (settled 2026-08-06, alternatives rejected):
 * - Registry entries carry a declarative `paramType` discriminator rather
 *   than per-entry parse/serialize closures; the loops below are generic, so
 *   a new registry entry needs zero edits here.
 * - Deterministic registry-driven tests rather than fast-check properties.
 * - Client state stays on individual `useQueryState` calls (not
 *   `useQueryStates`); parsers are generated from the registry instead.
 */

import { tryParseJson } from "@/lib/utils";
import type { Coords } from "@/types/places";
import { FILTER_REGISTRY, type FilterRegistryKey } from "./config";
import {
  CLUB_TYPE_FILTER,
  MEMBERSHIP_FILTER,
  RADIUS_VALUES,
  type AGE_GROUP_FILTER,
  type MEMBERSHIP_FEE_FILTER,
  type SKILL_LEVEL_FILTER,
  type CLUB_SORT,
} from "./filterConstants";
import type {
  EnumArrayFilterConfig,
  EnumFilterConfig,
  FilterConfig,
} from "./types";

/** The registry entries seen at the wire level, where every value is a string. */
const REGISTRY_CONFIGS = Object.values(
  FILTER_REGISTRY,
) as ReadonlyArray<FilterConfig>;

type SelectionFieldOf<C> =
  C extends EnumArrayFilterConfig<infer T>
    ? T[]
    : C extends EnumFilterConfig<infer T>
      ? T
      : never;

export type DiscoverSelection = {
  [K in FilterRegistryKey]: SelectionFieldOf<(typeof FILTER_REGISTRY)[K]>;
} & {
  activities: string[];
  searchQuery: string;
};

export type SelectionSearchParams =
  | URLSearchParams
  | Record<string, string | string[] | undefined>;

/** Club request params, produced only by {@link toClubParams}. */
export interface ClubFilterParams {
  location: Coords;
  activities?: string[];
  membership?: { isAcceptingSignups?: boolean };
  ageGroups?: AGE_GROUP_FILTER[];
  skillLevels?: SKILL_LEVEL_FILTER[];
  membershipFee?: MEMBERSHIP_FEE_FILTER[];
  spondOnly?: boolean;
  searchQuery?: string;
  sort?: CLUB_SORT;
  radiusKm?: number;
}

export const ACTIVITIES_PARAM = "activities";
export const SEARCH_QUERY_PARAM = "q";
const LEGACY_F_PARAM = "f";

function getParam(
  searchParams: SelectionSearchParams,
  key: string,
): string | undefined {
  if (searchParams instanceof URLSearchParams) {
    return searchParams.get(key) ?? undefined;
  }
  const value = searchParams[key];
  return Array.isArray(value) ? value[0] : value;
}

const normalizeActivities = (values: string[]): string[] => [
  ...new Set(values.map((value) => value.trim()).filter(Boolean)),
];

/**
 * Lenient read of the legacy `f` JSON blob: each usable field is extracted
 * independently, so a corrupt field (or corrupt JSON) never discards the
 * rest. Legacy `membership` is an `{ isAcceptingSignups }` object and is
 * normalized to the flat value-list encoding. The legacy shared `sort` field
 * is intentionally not read.
 */
function readLegacyF(rawF: string | undefined): {
  fields: Record<string, string>;
  activities?: string[];
} {
  const parsed = tryParseJson<Record<string, unknown>>(rawF ?? "");
  const blob =
    parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};

  const fields: Record<string, string> = {};
  for (const [key, value] of Object.entries(blob)) {
    if (typeof value === "string") {
      fields[key] = value;
    }
  }
  delete fields.sort;

  const membership = blob.membership as
    | { isAcceptingSignups?: unknown }
    | undefined;
  if (membership?.isAcceptingSignups === true) {
    fields.membership = MEMBERSHIP_FILTER.IS_ACCEPTING_SIGNUPS;
  }

  const activities = Array.isArray(blob.activities)
    ? blob.activities.filter((a): a is string => typeof a === "string")
    : undefined;

  return { fields, activities };
}

/** Parse a comma list leniently into valid values in registry value order. */
export function parseEnumArrayValue<T extends string>(
  raw: string,
  values: readonly T[],
): T[] {
  const allowed = new Set<string>(values);
  const enabled = new Set(
    raw
      .split(",")
      .map((value) => value.trim())
      .filter((value) => allowed.has(value)),
  );
  return values.filter((value) => enabled.has(value));
}

/** Canonical comma-list encoding: registry value order, duplicates dropped. */
export function serializeEnumArrayValue<T extends string>(
  selected: readonly T[],
  values: readonly T[],
): string {
  const selectedSet = new Set(selected);
  return values.filter((value) => selectedSet.has(value)).join(",");
}

function parseEnumParam<T extends string>(
  raw: string | undefined,
  config: EnumFilterConfig<T>,
): T {
  return raw !== undefined && (config.values as readonly string[]).includes(raw)
    ? (raw as T)
    : config.defaultValue;
}

function parseActivities(raw: string | undefined): string[] | undefined {
  if (raw === undefined) return undefined;
  return normalizeActivities(raw.split(","));
}

/**
 * On `/[location]/[activity]` routes the activity is a route segment, not a
 * URL param, so `parseSelection(searchParams)` alone never sees it — this
 * mirrors the server's route-activity merge (renderHomePage.tsx)
 * for client-side reads. The route activity always wins over any parsed one.
 */
export function withRouteActivity(
  selection: DiscoverSelection,
  routeActivityName: string | undefined,
): DiscoverSelection {
  return routeActivityName
    ? { ...selection, activities: normalizeActivities([routeActivityName]) }
    : selection;
}

export function parseSelection(
  searchParams: SelectionSearchParams,
): DiscoverSelection {
  const legacy = readLegacyF(getParam(searchParams, LEGACY_F_PARAM));
  const selection: Record<string, unknown> = {};

  for (const config of REGISTRY_CONFIGS) {
    const raw =
      getParam(searchParams, config.queryKey) ?? legacy.fields[config.queryKey];
    selection[config.key] =
      config.paramType === "enumArray"
        ? parseEnumArrayValue(raw ?? "", config.values)
        : parseEnumParam(raw, config);
  }

  selection.activities =
    parseActivities(getParam(searchParams, ACTIVITIES_PARAM)) ??
    legacy.activities ??
    [];
  selection.searchQuery =
    getParam(searchParams, SEARCH_QUERY_PARAM) ??
    legacy.fields.searchQuery ??
    "";

  return selection as DiscoverSelection;
}

/** The all-defaults selection — what a fresh search starts from. */
export function defaultSelection(): DiscoverSelection {
  return parseSelection(new URLSearchParams());
}

/**
 * Serialize a selection to its canonical URL params: registry order,
 * multi-select values in registry value order, defaults omitted.
 */
export function serializeSelection(
  selection: DiscoverSelection,
): URLSearchParams {
  const fields = selection as Record<string, string | string[]>;
  const params = new URLSearchParams();

  for (const config of REGISTRY_CONFIGS) {
    if (config.paramType === "enumArray") {
      const canonical = serializeEnumArrayValue(
        fields[config.key] as string[],
        config.values,
      );
      if (canonical !== "") {
        params.set(config.queryKey, canonical);
      }
    } else if (fields[config.key] !== config.defaultValue) {
      params.set(config.queryKey, fields[config.key] as string);
    }
  }

  const activities = normalizeActivities(selection.activities);
  if (activities.length > 0) {
    params.set(ACTIVITIES_PARAM, activities.join(","));
  }
  if (selection.searchQuery !== "") {
    params.set(SEARCH_QUERY_PARAM, selection.searchQuery);
  }

  return params;
}

/** UI-edge derivation: expand selected values into the checkbox record shape. */
export function toBoolRecord<T extends string>(
  values: readonly T[],
  selected: readonly T[],
): Record<T, boolean> {
  const selectedSet = new Set(selected);
  return Object.fromEntries(
    values.map((value) => [value, selectedSet.has(value)]),
  ) as Record<T, boolean>;
}

/** The legacy `{ isAcceptingSignups }` shape club queries and UI props consume. */
export function toMembershipParam(
  selection: DiscoverSelection,
): { isAcceptingSignups: true } | undefined {
  return selection.membership.includes(MEMBERSHIP_FILTER.IS_ACCEPTING_SIGNUPS)
    ? { isAcceptingSignups: true }
    : undefined;
}

export function isSpondOnly(selection: DiscoverSelection): boolean {
  return selection.clubType.includes(CLUB_TYPE_FILTER.SPOND_ONLY);
}

/** Project the club-category slice of a selection into request params. */
export function toClubParams(
  selection: DiscoverSelection,
  location: Coords,
): ClubFilterParams {
  return {
    location,
    activities: selection.activities,
    membership: toMembershipParam(selection),
    ageGroups: selection.ageGroup,
    skillLevels: selection.skillLevel,
    membershipFee: selection.membershipFee,
    spondOnly: isSpondOnly(selection),
    searchQuery: selection.searchQuery || undefined,
    sort: selection.clubSort,
    radiusKm: RADIUS_VALUES[selection.radius],
  };
}
